import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'
import { useAppTheme } from '@/theme/ThemeContext'
import { getAlertTypeOpenAlerts } from '@/services/notificationService'

export type CloseOpenAlertsMode = 'disable' | 'delete'

type Props = {
  open: boolean
  mode: CloseOpenAlertsMode
  siteId: string
  alertTypeId: number
  alertTypeName: string
  /** Runs the disable or delete. Rejects when it fails; the dialog then stays open with an error. */
  onConfirm: (closeOpenAlerts: boolean) => Promise<void>
  onCancel: () => void
}

const WORDING = {
  disable: {
    title: 'השבתת סוג התראה',
    action: 'השבתה',
    lead: 'השבתת סוג התראה זה תגרום לכך שלא יוערך עוד על מונים.',
    stays: 'ההתראות הפתוחות יישארו פתוחות עד שסוג ההתראה יופעל שוב.',
  },
  delete: {
    title: 'מחיקת סוג התראה',
    action: 'מחיקה',
    lead: 'מחיקת סוג התראה זה תגרום לכך שלא יוערך עוד על מונים.',
    stays: '',
  },
} as const

const DELETE_CLOSES = 'ההתראות הפתוחות ייסגרו בכוח עם המחיקה.'
const QUESTION = 'האם לסגור בכוח את ההתראות מסוג זה שפתוחות כעת?'

const nameMatches = (typed: string, name: string) =>
  typed.trim() === name.trim()

function openCountText(count: number | null | 'error') {
  if (count === null) return 'בודק כמה התראות פתוחות…'
  if (count === 'error') return 'לא ניתן לבדוק כמה התראות מסוג זה פתוחות כעת.'
  if (count === 0) return 'אין כרגע התראות פתוחות מסוג זה.'
  if (count === 1) return 'כרגע פתוחה התראה אחת מסוג זה.'
  return `כרגע פתוחות ${count} התראות מסוג זה.`
}

// Shown when a user turns an alert type off or deletes it. Disable asks yes or no. "Yes" closes the open
// alerts of the type in the same request, so it asks for the type's name first. Delete has no choice: it
// always closes them and always asks for the name.
const CloseOpenAlertsDialog = ({
  open,
  mode,
  siteId,
  alertTypeId,
  alertTypeName,
  onConfirm,
  onCancel,
}: Props) => {
  const { mode: theme } = useAppTheme()
  const isDarkMode = theme === 'dark'
  const wording = WORDING[mode]

  const [chooseClose, setChooseClose] = useState(false)
  const [typed, setTyped] = useState('')
  const [count, setCount] = useState<number | null | 'error'>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setChooseClose(false)
    setTyped('')
    setError(null)
    setCount(null)
    let current = true
    getAlertTypeOpenAlerts(siteId, alertTypeId)
      .then(n => current && setCount(n))
      .catch(() => current && setCount('error'))
    return () => {
      current = false
    }
  }, [open, mode, siteId, alertTypeId])

  const colors = {
    dialogBg: isDarkMode ? '#162645' : '#FFFFFF',
    titleColor: isDarkMode ? '#fff' : '#0F3E66',
    textColor: isDarkMode ? '#E2E8F0' : '#1F2937',
    subColor: isDarkMode ? '#94A3B8' : '#64748B',
  }

  const closeAlerts = mode === 'delete' || chooseClose
  const canConfirm =
    !busy && (!closeAlerts || nameMatches(typed, alertTypeName))

  const handleConfirm = async () => {
    if (!canConfirm) return
    setBusy(true)
    setError(null)
    try {
      await onConfirm(closeAlerts)
    } catch {
      setError('הפעולה נכשלה. נסו שוב.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      fullWidth
      maxWidth="sm"
      dir="rtl"
      PaperProps={{ sx: { borderRadius: '14px', bgcolor: colors.dialogBg } }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          color: colors.titleColor,
          fontFamily: 'Rubik, sans-serif',
        }}
      >
        {wording.title}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            fontFamily: 'Rubik, sans-serif',
          }}
        >
          <Typography sx={{ color: colors.textColor, fontFamily: 'inherit' }}>
            {wording.lead} {mode === 'disable' ? QUESTION : DELETE_CLOSES}
          </Typography>
          <Typography
            role="status"
            sx={{
              color: colors.subColor,
              fontFamily: 'inherit',
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {openCountText(count)}
          </Typography>
          {mode === 'disable' && (
            <RadioGroup
              value={chooseClose ? 'yes' : 'no'}
              onChange={e => setChooseClose(e.target.value === 'yes')}
            >
              <FormControlLabel
                value="yes"
                control={<Radio color="primary" />}
                label="כן, לסגור את ההתראות הפתוחות"
                sx={{ color: colors.textColor }}
              />
              <FormControlLabel
                value="no"
                control={<Radio color="primary" />}
                label="לא"
                sx={{ color: colors.textColor }}
              />
            </RadioGroup>
          )}
          {closeAlerts ? (
            <>
              <Typography
                sx={{
                  color: colors.textColor,
                  fontFamily: 'inherit',
                  fontSize: 14,
                }}
              >
                להקלדת שם סוג ההתראה לאישור: <strong>{alertTypeName}</strong>
              </Typography>
              <TextField
                label="שם סוג ההתראה"
                value={typed}
                onChange={e => setTyped(e.target.value)}
                fullWidth
                autoComplete="off"
                slotProps={{
                  htmlInput: { dir: 'rtl' },
                  inputLabel: { sx: { color: colors.subColor } },
                }}
                sx={{ '& .MuiInputBase-input': { color: colors.textColor } }}
              />
            </>
          ) : (
            <Typography
              sx={{
                color: colors.subColor,
                fontFamily: 'inherit',
                fontSize: 14,
              }}
            >
              {wording.stays}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onCancel} disabled={busy}>
          ביטול
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={!canConfirm}
          variant="contained"
          color={mode === 'delete' ? 'error' : 'primary'}
          startIcon={busy ? <CircularProgress size={16} /> : undefined}
        >
          {wording.action}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CloseOpenAlertsDialog
